import { Eye, EyeOff, Check } from 'lucide-react';
import { useState } from 'react';

interface FormInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder: string;
  icon: any;
  type?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  helperText?: string;
  showSuccess?: boolean;
  isPassword?: boolean;
  ariaLabel?: string;
}

export function FormInput({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  icon: Icon,
  type = 'text',
  error,
  disabled,
  required,
  autoFocus,
  helperText,
  showSuccess,
  isPassword,
  ariaLabel,
}: FormInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;
  const isValid = showSuccess && value && !error;

  return (
    <div className="space-y-2">
      <label className="text-sm font-semibold text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        <input
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={ariaLabel || label}
          aria-invalid={!!error}
          aria-describedby={error ? `${label}-error` : helperText ? `${label}-helper` : undefined}
          className={`w-full pl-10 ${isPassword || isValid ? 'pr-10' : 'pr-4'} py-3 bg-white border ${
            error
              ? 'border-red-500 focus:ring-red-500'
              : isValid
              ? 'border-green-500 focus:ring-green-500'
              : 'border-gray-300 focus:ring-primary-500'
          } text-gray-900 text-sm outline-none focus:ring-2 focus:ring-offset-1 focus:border-transparent transition-all placeholder:text-gray-400 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed`}
          placeholder={placeholder}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded p-1"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
        {isValid && !isPassword && (
          <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600 pointer-events-none" />
        )}
      </div>
      {error && (
        <p id={`${label}-error`} className="text-xs text-red-600 ml-1 flex items-center gap-1">
          <span className="inline-block w-1 h-1 rounded-full bg-red-600" />
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={`${label}-helper`} className="text-xs text-gray-600 ml-1">
          {helperText}
        </p>
      )}
    </div>
  );
}
